import React, { useState, useEffect, useRef } from "react";
import {
  Container,
  Button,
  Row,
  Col,
  Card,
  Modal,
  ListGroup,
  Badge,
} from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";

function Home() {
  const navigate = useNavigate();
  const [activeFilter, setActiveFilter] = useState("all");
  const [showModal, setShowModal] = useState(false);
  const [selectedArea, setSelectedArea] = useState(null);
  const [email, setEmail] = useState("");
  const [emailSubmitted, setEmailSubmitted] = useState(false);

  // Intersection Observer for scroll animations
  useEffect(() => {
    const observerOptions = {
      threshold: 0.1,
      rootMargin: "0px 0px -100px 0px",
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add("fade-in-visible");
        }
      });
    }, observerOptions);

    // Observe all sections with fade-in class
    const sections = document.querySelectorAll(".fade-in-section");
    sections.forEach((section) => observer.observe(section));

    return () => observer.disconnect();
  }, []);

  // Handle newsletter signup
  const handleNewsletterSignup = (e) => {
    e.preventDefault();
    if (email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setEmailSubmitted(true);
      setTimeout(() => {
        setEmailSubmitted(false);
        setEmail("");
      }, 3000);
    }
  };

  // Stats data
  const stats = [
    { number: "3+", label: "Năm Kinh Nghiệm", icon: "bi-calendar-check" },
    { number: "500+", label: "Khách Hàng Hài Lòng", icon: "bi-people" },
    { number: "30+", label: "Chỗ Ngồi Premium", icon: "bi-laptop" },
    { number: "24/7", label: "Hỗ Trợ Khách Hàng", icon: "bi-headset" },
  ];

  // Services data
  const services = [
    {
      num: "01",
      title: "Không Gian Làm Việc",
      desc: "Bàn làm việc cá nhân với đầy đủ ổ cắm điện và wifi tốc độ cao.",
      icon: "bi-laptop",
    },
    {
      num: "02",
      title: "Phòng Họp",
      desc: "Phòng họp hiện đại với máy chiếu, bảng trắng và cách âm tốt.",
      icon: "bi-people-fill",
    },
    {
      num: "03",
      title: "Góc Yên Tĩnh",
      desc: "Khu vực yên tĩnh tuyệt đối cho công việc cần tập trung cao độ.",
      icon: "bi-book",
    },
    {
      num: "04",
      title: "Cà Phê Thượng Hạng",
      desc: "Đồ uống chất lượng cao từ hạt cà phê được tuyển chọn kỹ lưỡng.",
      icon: "bi-cup-hot",
    },
    {
      num: "05",
      title: "Sự Kiện & Workshop",
      desc: "Tổ chức sự kiện, workshop và các buổi networking chuyên nghiệp.",
      icon: "bi-calendar-event",
    },
    {
      num: "06",
      title: "Dịch Vụ In Ấn",
      desc: "Hỗ trợ in ấn, scan tài liệu với thiết bị hiện đại.",
      icon: "bi-printer",
    },
  ];

  // Features data
  const features = [
    {
      icon: "bi-wifi",
      title: "Wifi Siêu Tốc",
      desc: "Đường truyền 1Gbps ổn định",
    },
    {
      icon: "bi-shield-check",
      title: "An Ninh 24/7",
      desc: "Camera giám sát và bảo vệ",
    },
    {
      icon: "bi-graph-up-arrow",
      title: "Năng Suất Cao",
      desc: "Không gian tối ưu cho công việc",
    },
    {
      icon: "bi-people",
      title: "Cộng Đồng",
      desc: "Kết nối với các chuyên gia",
    },
    {
      icon: "bi-cloud-check",
      title: "Đặt Chỗ Online",
      desc: "Hệ thống booking thông minh",
    },
    {
      icon: "bi-gear",
      title: "Tiện Nghi Đầy Đủ",
      desc: "Máy chiếu, bảng trắng, ổ cắm",
    },
  ];

  // Gallery/Portfolio data
  const gallery = [
    {
      id: 1,
      img: "https://images.unsplash.com/photo-1497366216548-37526070297c?auto=format&fit=crop&q=80&w=600",
      title: "Không Gian Chung",
      category: "workspace",
    },
    {
      id: 2,
      img: "https://images.unsplash.com/photo-1527192491265-7e15c55b1ed2?auto=format&fit=crop&q=80&w=600",
      title: "Góc Yên Tĩnh",
      category: "quiet",
    },
    {
      id: 3,
      img: "https://images.unsplash.com/photo-1497215842964-222b430dc0a0?auto=format&fit=crop&q=80&w=600",
      title: "Phòng Họp",
      category: "meeting",
    },
    {
      id: 4,
      img: "https://images.unsplash.com/photo-1554118811-1e0d58224f24?auto=format&fit=crop&q=80&w=600",
      title: "Quầy Cà Phê",
      category: "cafe",
    },
    {
      id: 5,
      img: "https://images.unsplash.com/photo-1521737711867-e3b97375f902?auto=format&fit=crop&q=80&w=600",
      title: "Làm Việc Nhóm",
      category: "workspace",
    },
    {
      id: 6,
      img: "https://images.unsplash.com/photo-1517502884422-41eaead166d4?auto=format&fit=crop&q=80&w=600",
      title: "Workshop",
      category: "meeting",
    },
  ];

  // Pricing data
  const pricing = [
    {
      name: "Theo Giờ",
      price: "25K",
      period: "/giờ",
      features: [
        "Wifi tốc độ cao",
        "Nước lọc miễn phí",
        "Ổ cắm điện",
        "Không gian chung",
      ],
      highlight: false,
    },
    {
      name: "Nửa Ngày",
      price: "80K",
      period: "/4 giờ",
      features: [
        "Tất cả gói Theo Giờ",
        "1 đồ uống miễn phí",
        "Góc yên tĩnh",
        "In ấn 10 trang",
      ],
      highlight: true,
    },
    {
      name: "Cả Ngày",
      price: "150K",
      period: "/ngày",
      features: [
        "Tất cả gói Nửa Ngày",
        "2 đồ uống miễn phí",
        "Phòng họp 1 giờ",
        "Ưu tiên chỗ ngồi",
      ],
      highlight: false,
    },
  ];

  // Team data
  const team = [
    {
      name: "Nguyễn Văn A",
      role: "Founder & CEO",
      img: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?auto=format&fit=crop&q=80&w=200",
    },
    {
      name: "Trần Thị B",
      role: "Operations Manager",
      img: "https://images.unsplash.com/photo-1494790108377-be9c29b29330?auto=format&fit=crop&q=80&w=200",
    },
    {
      name: "Lê Văn C",
      role: "Head Barista",
      img: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?auto=format&fit=crop&q=80&w=200",
    },
    {
      name: "Phạm Thị D",
      role: "Customer Success",
      img: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?auto=format&fit=crop&q=80&w=200",
    },
  ];

  // Area details for modal
  const areaDetails = {
    workspace: {
      title: "Không Gian Làm Việc",
      description: "Bàn làm việc thoáng đãng với đầy đủ tiện nghi.",
      tables: [
        { id: "W01", status: "available" },
        { id: "W02", status: "occupied" },
        { id: "W03", status: "available" },
      ],
    },
    quiet: {
      title: "Góc Yên Tĩnh",
      description: "Khu vực yên tĩnh tuyệt đối cho công việc tập trung.",
      tables: [
        { id: "Q01", status: "available" },
        { id: "Q02", status: "occupied" },
      ],
    },
    meeting: {
      title: "Phòng Họp",
      description: "Phòng họp hiện đại với đầy đủ thiết bị.",
      tables: [
        { id: "M01", status: "available" },
        { id: "M02", status: "available" },
      ],
    },
  };

  const handleShowDetails = (category) => {
    if (areaDetails[category]) {
      setSelectedArea(category);
      setShowModal(true);
    }
  };

  const handleClose = () => setShowModal(false);
  const handleBookingRedirect = () => {
    handleClose();
    navigate("/booking");
  };

  const filteredGallery =
    activeFilter === "all"
      ? gallery
      : gallery.filter((item) => item.category === activeFilter);

  return (
    <div style={{ backgroundColor: "#fff" }} id="main-content">
      <style>{`
        body {
          padding-top: 0 !important;
        }
        
        .fade-in-section {
          opacity: 0;
          transform: translateY(50px);
          transition: opacity 0.8s ease-out, transform 0.8s ease-out;
        }
        
        .fade-in-visible {
          opacity: 1;
          transform: translateY(0);
        }
        
        .stagger-1 { transition-delay: 0.1s; }
        .stagger-2 { transition-delay: 0.2s; }
        .stagger-3 { transition-delay: 0.3s; }
        .stagger-4 { transition-delay: 0.4s; }
        .stagger-5 { transition-delay: 0.5s; }
        .stagger-6 { transition-delay: 0.6s; }
      `}</style>

      {/* ==================== HERO SECTION ==================== */}
      <section
        style={{
          minHeight: "100vh",
          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          display: "flex",
          alignItems: "center",
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* Decorative shapes */}
        <div
          style={{
            position: "absolute",
            top: "-50%",
            right: "-20%",
            width: "800px",
            height: "800px",
            background: "rgba(255,255,255,0.1)",
            borderRadius: "50%",
          }}
        ></div>
        <div
          style={{
            position: "absolute",
            bottom: "-30%",
            left: "-10%",
            width: "600px",
            height: "600px",
            background: "rgba(255,255,255,0.05)",
            borderRadius: "50%",
          }}
        ></div>

        <Container style={{ position: "relative", zIndex: 2 }}>
          <Row className="align-items-center">
            <Col lg={6} className="text-white mb-5 mb-lg-0">
              <span
                className="badge mb-3 px-3 py-2"
                style={{
                  background: "rgba(255,255,255,0.2)",
                  fontSize: "0.9rem",
                  fontWeight: "500",
                }}
              >
                <i className="bi bi-stars me-2"></i>Premium Coworking Space
              </span>
              <h1
                className="display-3 fw-bold mb-4"
                style={{ lineHeight: "1.2" }}
              >
                Không Gian Sáng Tạo,
                <br />
                <span style={{ color: "#ffd700" }}>Năng Suất Tối Đa</span>
              </h1>
              <p
                className="lead mb-4 opacity-90"
                style={{ fontSize: "1.2rem", maxWidth: "500px" }}
              >
                UC Study Coffee - Nơi kết hợp hoàn hảo giữa không gian làm việc
                chuyên nghiệp và hương vị cà phê thượng hạng.
              </p>
              <div className="d-flex gap-3 flex-wrap">
                <Button
                  as={Link}
                  to="/booking"
                  size="lg"
                  className="px-5 py-3 fw-semibold border-0 shadow-lg"
                  style={{
                    background: "#fff",
                    color: "#667eea",
                    borderRadius: "50px",
                    transition: "all 0.3s ease",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = "translateY(-3px)";
                    e.currentTarget.style.boxShadow =
                      "0 12px 24px rgba(0,0,0,0.2)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = "translateY(0)";
                    e.currentTarget.style.boxShadow =
                      "0 8px 16px rgba(0,0,0,0.1)";
                  }}
                >
                  <i className="bi bi-calendar-check me-2"></i>Đặt Chỗ Ngay
                </Button>
                <Button
                  as={Link}
                  to="/menu"
                  variant="outline-light"
                  size="lg"
                  className="px-5 py-3 fw-semibold"
                  style={{
                    borderRadius: "50px",
                    borderWidth: "2px",
                    transition: "all 0.3s ease",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = "rgba(255,255,255,0.1)";
                    e.currentTarget.style.transform = "translateY(-3px)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = "transparent";
                    e.currentTarget.style.transform = "translateY(0)";
                  }}
                >
                  <i className="bi bi-cup-hot me-2"></i>Khám Phá Menu
                </Button>
              </div>
              {/* Trust badges */}
              <div className="d-flex gap-4 mt-4 flex-wrap">
                <div className="d-flex align-items-center gap-2 text-white-50">
                  <i className="bi bi-shield-check fs-5"></i>
                  <small className="fw-medium">Bảo mật 24/7</small>
                </div>
                <div className="d-flex align-items-center gap-2 text-white-50">
                  <i className="bi bi-wifi fs-5"></i>
                  <small className="fw-medium">Wifi 1Gbps</small>
                </div>
                <div className="d-flex align-items-center gap-2 text-white-50">
                  <i
                    className="bi bi-star-fill fs-5"
                    style={{ color: "#ffd700" }}
                  ></i>
                  <small className="fw-medium">4.9/5 đánh giá</small>
                </div>
              </div>
            </Col>
            <Col lg={6}>
              <div style={{ position: "relative" }}>
                <img
                  src="https://images.unsplash.com/photo-1497366216548-37526070297c?auto=format&fit=crop&q=80&w=800"
                  alt="Workspace"
                  className="img-fluid rounded-4 shadow-lg"
                  style={{ transform: "rotate(3deg)" }}
                />
                <div
                  className="bg-white p-3 rounded-3 shadow-lg"
                  style={{
                    position: "absolute",
                    bottom: "-20px",
                    left: "-20px",
                    maxWidth: "200px",
                  }}
                >
                  <div className="d-flex align-items-center gap-2">
                    <div
                      style={{
                        width: "50px",
                        height: "50px",
                        background: "linear-gradient(135deg, #667eea, #764ba2)",
                        borderRadius: "50%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <i className="bi bi-people-fill text-white fs-5"></i>
                    </div>
                    <div>
                      <div className="fw-bold" style={{ color: "#333" }}>
                        500+
                      </div>
                      <small className="text-muted">Khách hàng tin tưởng</small>
                    </div>
                  </div>
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* ==================== STATS SECTION ==================== */}
      <section
        className="py-0 fade-in-section"
        style={{ marginTop: "-60px", position: "relative", zIndex: 10 }}
      >
        <Container>
          <Card
            className="border-0 shadow-lg"
            style={{ borderRadius: "20px", overflow: "hidden" }}
          >
            <Card.Body className="p-0">
              <Row className="g-0 text-center">
                {stats.map((stat, index) => (
                  <Col md={3} key={index}>
                    <div
                      className={`p-4 ${
                        index < 3 ? "border-end" : ""
                      } stat-card`}
                      style={{
                        borderColor: "#eee !important",
                        transition: "all 0.3s ease",
                        cursor: "pointer",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background =
                          "linear-gradient(135deg, #f8f9ff, #fff)";
                        e.currentTarget.style.transform = "translateY(-5px)";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = "transparent";
                        e.currentTarget.style.transform = "translateY(0)";
                      }}
                    >
                      <i
                        className={`bi ${stat.icon} fs-2 mb-2`}
                        style={{
                          color: "#667eea",
                          transition: "all 0.3s ease",
                        }}
                      ></i>
                      <h2
                        className="fw-bold mb-1 counter"
                        style={{
                          color: "#333",
                          fontSize: "2.5rem",
                        }}
                      >
                        {stat.number}
                      </h2>
                      <p className="text-muted mb-0 small fw-medium">
                        {stat.label}
                      </p>
                    </div>
                  </Col>
                ))}
              </Row>
            </Card.Body>
          </Card>
        </Container>
      </section>

      {/* ==================== ABOUT SECTION ==================== */}
      <section className="py-5 fade-in-section" style={{ marginTop: "60px" }}>
        <Container>
          <Row className="align-items-center">
            <Col lg={6} className="mb-4 mb-lg-0">
              <div style={{ position: "relative" }}>
                <img
                  src="https://images.unsplash.com/photo-1527192491265-7e15c55b1ed2?auto=format&fit=crop&q=80&w=600"
                  alt="About Us"
                  className="img-fluid rounded-4 shadow"
                />
                <div
                  className="p-4 bg-white rounded-3 shadow-lg"
                  style={{
                    position: "absolute",
                    bottom: "20px",
                    right: "-30px",
                    maxWidth: "250px",
                  }}
                >
                  <div className="d-flex align-items-center gap-3">
                    <div
                      style={{
                        width: "60px",
                        height: "60px",
                        background: "linear-gradient(135deg, #667eea, #764ba2)",
                        borderRadius: "15px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <i className="bi bi-award-fill text-white fs-4"></i>
                    </div>
                    <div>
                      <h4 className="fw-bold mb-0" style={{ color: "#667eea" }}>
                        3+ Năm
                      </h4>
                      <small className="text-muted">Kinh nghiệm phục vụ</small>
                    </div>
                  </div>
                </div>
              </div>
            </Col>
            <Col lg={6}>
              <span
                className="text-uppercase fw-semibold"
                style={{
                  color: "#667eea",
                  letterSpacing: "2px",
                  fontSize: "0.85rem",
                }}
              >
                Về Chúng Tôi
              </span>
              <h2
                className="display-5 fw-bold mt-2 mb-4"
                style={{ color: "#333" }}
              >
                Xây Dựng Không Gian
                <br />
                <span style={{ color: "#667eea" }}>Cho Sự Thành Công</span>
              </h2>
              <p
                className="text-muted mb-4"
                style={{ fontSize: "1.1rem", lineHeight: "1.8" }}
              >
                UC Study Coffee ra đời với sứ mệnh tạo ra một không gian làm
                việc và học tập lý tưởng cho cộng đồng. Chúng tôi tin rằng môi
                trường làm việc tốt là nền tảng cho sự sáng tạo và thành công.
              </p>
              <Row className="g-3 mb-4">
                <Col sm={6}>
                  <div className="d-flex align-items-center gap-2">
                    <i
                      className="bi bi-check-circle-fill"
                      style={{ color: "#667eea" }}
                    ></i>
                    <span>Wifi tốc độ cao 1Gbps</span>
                  </div>
                </Col>
                <Col sm={6}>
                  <div className="d-flex align-items-center gap-2">
                    <i
                      className="bi bi-check-circle-fill"
                      style={{ color: "#667eea" }}
                    ></i>
                    <span>Không gian yên tĩnh</span>
                  </div>
                </Col>
                <Col sm={6}>
                  <div className="d-flex align-items-center gap-2">
                    <i
                      className="bi bi-check-circle-fill"
                      style={{ color: "#667eea" }}
                    ></i>
                    <span>Cà phê chất lượng cao</span>
                  </div>
                </Col>
                <Col sm={6}>
                  <div className="d-flex align-items-center gap-2">
                    <i
                      className="bi bi-check-circle-fill"
                      style={{ color: "#667eea" }}
                    ></i>
                    <span>Phòng họp hiện đại</span>
                  </div>
                </Col>
              </Row>
              <Button
                as={Link}
                to="/booking"
                className="px-4 py-3 fw-semibold border-0"
                style={{
                  background: "linear-gradient(135deg, #667eea, #764ba2)",
                  borderRadius: "50px",
                }}
              >
                Tìm Hiểu Thêm <i className="bi bi-arrow-right ms-2"></i>
              </Button>
            </Col>
          </Row>
        </Container>
      </section>

      {/* ==================== SERVICES SECTION ==================== */}
      <section
        className="py-5 fade-in-section"
        style={{ background: "#f8f9fa" }}
      >
        <Container>
          <div className="text-center mb-5">
            <span
              className="text-uppercase fw-semibold"
              style={{
                color: "#667eea",
                letterSpacing: "2px",
                fontSize: "0.85rem",
              }}
            >
              Dịch Vụ
            </span>
            <h2 className="display-5 fw-bold mt-2" style={{ color: "#333" }}>
              Những Gì Chúng Tôi Cung Cấp
            </h2>
            <p className="text-muted mx-auto" style={{ maxWidth: "600px" }}>
              Trải nghiệm đầy đủ các dịch vụ chuyên nghiệp được thiết kế để nâng
              cao hiệu suất làm việc của bạn.
            </p>
          </div>
          <Row className="g-4">
            {services.map((service, index) => (
              <Col md={6} lg={4} key={index}>
                <Card
                  className={`h-100 border-0 shadow-sm fade-in-section stagger-${
                    (index % 6) + 1
                  }`}
                  style={{
                    borderRadius: "20px",
                    transition: "all 0.3s",
                    cursor: "pointer",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = "translateY(-10px)";
                    e.currentTarget.style.boxShadow =
                      "0 20px 40px rgba(102,126,234,0.2)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = "translateY(0)";
                    e.currentTarget.style.boxShadow =
                      "0 0.125rem 0.25rem rgba(0,0,0,0.075)";
                  }}
                >
                  <Card.Body className="p-4">
                    <div className="d-flex justify-content-between align-items-start mb-3">
                      <div
                        style={{
                          width: "60px",
                          height: "60px",
                          background:
                            "linear-gradient(135deg, #667eea, #764ba2)",
                          borderRadius: "15px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <i className={`bi ${service.icon} text-white fs-4`}></i>
                      </div>
                      <span
                        className="fw-bold"
                        style={{ fontSize: "2rem", color: "#eee" }}
                      >
                        {service.num}
                      </span>
                    </div>
                    <h5 className="fw-bold mb-2" style={{ color: "#333" }}>
                      {service.title}
                    </h5>
                    <p className="text-muted mb-0">{service.desc}</p>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* ==================== FEATURES SECTION ==================== */}
      <section className="py-5 fade-in-section">
        <Container>
          <Row className="align-items-center">
            <Col lg={6} className="mb-4 mb-lg-0">
              <span
                className="text-uppercase fw-semibold"
                style={{
                  color: "#667eea",
                  letterSpacing: "2px",
                  fontSize: "0.85rem",
                }}
              >
                Tính Năng
              </span>
              <h2
                className="display-5 fw-bold mt-2 mb-4"
                style={{ color: "#333" }}
              >
                Tại Sao Chọn
                <br />
                <span style={{ color: "#667eea" }}>UC Study Coffee?</span>
              </h2>
              <p className="text-muted mb-4" style={{ fontSize: "1.1rem" }}>
                Chúng tôi cung cấp môi trường làm việc tối ưu với đầy đủ tiện
                nghi hiện đại.
              </p>
              <Row className="g-3">
                {features.map((feature, index) => (
                  <Col sm={6} key={index}>
                    <div
                      className="d-flex align-items-start gap-3 p-3 bg-white rounded-3 shadow-sm"
                      style={{ transition: "all 0.3s" }}
                      onMouseEnter={(e) =>
                        (e.currentTarget.style.transform = "scale(1.02)")
                      }
                      onMouseLeave={(e) =>
                        (e.currentTarget.style.transform = "scale(1)")
                      }
                    >
                      <div
                        style={{
                          width: "45px",
                          height: "45px",
                          background:
                            "linear-gradient(135deg, #667eea, #764ba2)",
                          borderRadius: "10px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                        }}
                      >
                        <i className={`bi ${feature.icon} text-white`}></i>
                      </div>
                      <div>
                        <h6 className="fw-bold mb-1" style={{ color: "#333" }}>
                          {feature.title}
                        </h6>
                        <small className="text-muted">{feature.desc}</small>
                      </div>
                    </div>
                  </Col>
                ))}
              </Row>
            </Col>
            <Col lg={6}>
              <img
                src="https://images.unsplash.com/photo-1497215842964-222b430dc0a0?auto=format&fit=crop&q=80&w=600"
                alt="Features"
                className="img-fluid rounded-4 shadow-lg"
              />
            </Col>
          </Row>
        </Container>
      </section>

      {/* ==================== GALLERY/PORTFOLIO SECTION ==================== */}
      <section
        className="py-5 fade-in-section"
        style={{ background: "#f8f9fa" }}
      >
        <Container>
          <div className="text-center mb-5">
            <span
              className="text-uppercase fw-semibold"
              style={{
                color: "#667eea",
                letterSpacing: "2px",
                fontSize: "0.85rem",
              }}
            >
              Không Gian
            </span>
            <h2 className="display-5 fw-bold mt-2" style={{ color: "#333" }}>
              Khám Phá UC Space
            </h2>
          </div>

          {/* Filter Buttons */}
          <div className="d-flex justify-content-center gap-2 mb-4 flex-wrap">
            {[
              { key: "all", label: "Tất Cả" },
              { key: "workspace", label: "Workspace" },
              { key: "quiet", label: "Quiet Zone" },
              { key: "meeting", label: "Phòng Họp" },
              { key: "cafe", label: "Cafe" },
            ].map((filter) => (
              <Button
                key={filter.key}
                variant={
                  activeFilter === filter.key ? "primary" : "outline-secondary"
                }
                className="px-4 py-2"
                style={{
                  borderRadius: "50px",
                  background:
                    activeFilter === filter.key
                      ? "linear-gradient(135deg, #667eea, #764ba2)"
                      : "transparent",
                  border:
                    activeFilter === filter.key ? "none" : "1px solid #ddd",
                }}
                onClick={() => setActiveFilter(filter.key)}
              >
                {filter.label}
              </Button>
            ))}
          </div>

          {/* Gallery Grid */}
          <Row className="g-4">
            {filteredGallery.map((item, index) => (
              <Col md={6} lg={4} key={item.id}>
                <div
                  className={`position-relative overflow-hidden rounded-4 shadow fade-in-section stagger-${
                    (index % 6) + 1
                  }`}
                  style={{ cursor: "pointer", transition: "all 0.3s" }}
                  onMouseEnter={(e) =>
                    (e.currentTarget.style.transform = "scale(1.02)")
                  }
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.transform = "scale(1)")
                  }
                  onClick={() => handleShowDetails(item.category)}
                >
                  <img
                    src={item.img}
                    alt={item.title}
                    className="w-100"
                    style={{ height: "280px", objectFit: "cover" }}
                  />
                  <div
                    className="position-absolute bottom-0 start-0 end-0 p-3"
                    style={{
                      background:
                        "linear-gradient(transparent, rgba(0,0,0,0.8))",
                    }}
                  >
                    <h6 className="text-white mb-0 fw-bold">{item.title}</h6>
                  </div>
                </div>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* ==================== PRICING SECTION ==================== */}
      <section className="py-5 fade-in-section">
        <Container>
          <div className="text-center mb-5">
            <span
              className="text-uppercase fw-semibold"
              style={{
                color: "#667eea",
                letterSpacing: "2px",
                fontSize: "0.85rem",
              }}
            >
              Bảng Giá
            </span>
            <h2 className="display-5 fw-bold mt-2" style={{ color: "#333" }}>
              Gói Dịch Vụ Linh Hoạt
            </h2>
            <p className="text-muted mx-auto" style={{ maxWidth: "600px" }}>
              Chọn gói phù hợp với nhu cầu của bạn. Tất cả đều bao gồm wifi tốc
              độ cao và không gian thoải mái.
            </p>
          </div>
          <Row className="g-4 justify-content-center">
            {pricing.map((plan, index) => (
              <Col md={6} lg={4} key={index}>
                <Card
                  className={`h-100 border-0 text-center fade-in-section stagger-${
                    index + 1
                  } ${plan.highlight ? "shadow-lg" : "shadow-sm"}`}
                  style={{
                    borderRadius: "20px",
                    transform: plan.highlight ? "scale(1.05)" : "scale(1)",
                    background: plan.highlight
                      ? "linear-gradient(135deg, #667eea, #764ba2)"
                      : "#fff",
                    color: plan.highlight ? "#fff" : "#333",
                  }}
                >
                  <Card.Body className="p-5">
                    {plan.highlight && (
                      <Badge
                        className="mb-3 px-3 py-2"
                        style={{ background: "rgba(255,255,255,0.2)" }}
                      >
                        Phổ Biến Nhất
                      </Badge>
                    )}
                    <h5 className={`fw-bold ${plan.highlight ? "" : ""}`}>
                      {plan.name}
                    </h5>
                    <div className="my-4">
                      <span className="display-4 fw-bold">{plan.price}</span>
                      <span
                        className={plan.highlight ? "opacity-75" : "text-muted"}
                      >
                        {plan.period}
                      </span>
                    </div>
                    <ul className="list-unstyled mb-4">
                      {plan.features.map((feature, idx) => (
                        <li key={idx} className="mb-2">
                          <i
                            className={`bi bi-check-circle-fill me-2 ${
                              plan.highlight ? "" : ""
                            }`}
                            style={{
                              color: plan.highlight ? "#ffd700" : "#667eea",
                            }}
                          ></i>
                          {feature}
                        </li>
                      ))}
                    </ul>
                    <Button
                      as={Link}
                      to="/booking"
                      className="w-100 py-3 fw-semibold"
                      style={{
                        borderRadius: "50px",
                        background: plan.highlight
                          ? "#fff"
                          : "linear-gradient(135deg, #667eea, #764ba2)",
                        color: plan.highlight ? "#667eea" : "#fff",
                        border: "none",
                      }}
                    >
                      Chọn Gói Này
                    </Button>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* ==================== TEAM SECTION ==================== */}
      <section
        className="py-5 fade-in-section"
        style={{ background: "#f8f9fa" }}
      >
        <Container>
          <div className="text-center mb-5">
            <span
              className="text-uppercase fw-semibold"
              style={{
                color: "#667eea",
                letterSpacing: "2px",
                fontSize: "0.85rem",
              }}
            >
              Đội Ngũ
            </span>
            <h2 className="display-5 fw-bold mt-2" style={{ color: "#333" }}>
              Gặp Gỡ Team UC
            </h2>
          </div>
          <Row className="g-4 justify-content-center">
            {team.map((member, index) => (
              <Col md={6} lg={3} key={index}>
                <Card
                  className={`border-0 shadow-sm text-center h-100 fade-in-section stagger-${
                    index + 1
                  }`}
                  style={{ borderRadius: "20px", transition: "all 0.3s" }}
                  onMouseEnter={(e) =>
                    (e.currentTarget.style.transform = "translateY(-10px)")
                  }
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.transform = "translateY(0)")
                  }
                >
                  <Card.Body className="p-4">
                    <div className="mb-3">
                      <img
                        src={member.img}
                        alt={member.name}
                        className="rounded-circle"
                        style={{
                          width: "120px",
                          height: "120px",
                          objectFit: "cover",
                          border: "4px solid #667eea",
                        }}
                      />
                    </div>
                    <h5 className="fw-bold mb-1" style={{ color: "#333" }}>
                      {member.name}
                    </h5>
                    <p className="text-muted small mb-3">{member.role}</p>
                    <div className="d-flex justify-content-center gap-2">
                      <a
                        href="#"
                        className="btn btn-sm rounded-circle"
                        style={{
                          width: "35px",
                          height: "35px",
                          background: "#667eea",
                          color: "#fff",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <i className="bi bi-facebook"></i>
                      </a>
                      <a
                        href="#"
                        className="btn btn-sm rounded-circle"
                        style={{
                          width: "35px",
                          height: "35px",
                          background: "#667eea",
                          color: "#fff",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <i className="bi bi-linkedin"></i>
                      </a>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* ==================== CTA SECTION ==================== */}
      <section
        className="py-5 fade-in-section"
        style={{
          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
        }}
      >
        <Container className="text-center text-white py-4">
          <h2 className="display-5 fw-bold mb-3">Sẵn Sàng Bắt Đầu?</h2>
          <p className="lead mb-4 opacity-90">
            Đặt chỗ ngay hôm nay và trải nghiệm không gian làm việc tuyệt vời.
          </p>
          <div className="d-flex gap-3 justify-content-center flex-wrap">
            <Button
              as={Link}
              to="/booking"
              size="lg"
              className="px-5 py-3 fw-semibold border-0"
              style={{
                background: "#fff",
                color: "#667eea",
                borderRadius: "50px",
              }}
            >
              <i className="bi bi-calendar-check me-2"></i>Đặt Chỗ Ngay
            </Button>
            <Button
              as={Link}
              to="/menu"
              variant="outline-light"
              size="lg"
              className="px-5 py-3 fw-semibold"
              style={{ borderRadius: "50px", borderWidth: "2px" }}
            >
              <i className="bi bi-cup-hot me-2"></i>Xem Menu
            </Button>
          </div>
        </Container>
      </section>

      {/* ==================== CONTACT SECTION ==================== */}
      <section className="py-5 fade-in-section">
        <Container>
          <div className="text-center mb-5">
            <span
              className="text-uppercase fw-semibold"
              style={{
                color: "#667eea",
                letterSpacing: "2px",
                fontSize: "0.85rem",
              }}
            >
              Liên Hệ
            </span>
            <h2 className="display-5 fw-bold mt-2" style={{ color: "#333" }}>
              Kết Nối Với Chúng Tôi
            </h2>
          </div>
          <Row className="g-4">
            <Col md={4}>
              <Card
                className="border-0 shadow-sm h-100 text-center fade-in-section stagger-1"
                style={{ borderRadius: "20px" }}
              >
                <Card.Body className="p-4">
                  <div
                    className="mb-3"
                    style={{
                      width: "70px",
                      height: "70px",
                      background: "linear-gradient(135deg, #667eea, #764ba2)",
                      borderRadius: "50%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      margin: "0 auto",
                    }}
                  >
                    <i className="bi bi-map-pin text-white fs-4"></i>
                  </div>
                  <h5 className="fw-bold" style={{ color: "#333" }}>
                    Địa Chỉ
                  </h5>
                  <p className="text-muted mb-0">
                    123 Đường ABC, Quận 1<br />
                    TP. Hồ Chí Minh
                  </p>
                </Card.Body>
              </Card>
            </Col>
            <Col md={4}>
              <Card
                className="border-0 shadow-sm h-100 text-center fade-in-section stagger-2"
                style={{ borderRadius: "20px" }}
              >
                <Card.Body className="p-4">
                  <div
                    className="mb-3"
                    style={{
                      width: "70px",
                      height: "70px",
                      background: "linear-gradient(135deg, #667eea, #764ba2)",
                      borderRadius: "50%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      margin: "0 auto",
                    }}
                  >
                    <i className="bi bi-phone text-white fs-4"></i>
                  </div>
                  <h5 className="fw-bold" style={{ color: "#333" }}>
                    Điện Thoại
                  </h5>
                  <p className="text-muted mb-0">
                    Hotline: 1900 1234
                    <br />
                    Mobile: 090 123 4567
                  </p>
                </Card.Body>
              </Card>
            </Col>
            <Col md={4}>
              <Card
                className="border-0 shadow-sm h-100 text-center fade-in-section stagger-3"
                style={{ borderRadius: "20px" }}
              >
                <Card.Body className="p-4">
                  <div
                    className="mb-3"
                    style={{
                      width: "70px",
                      height: "70px",
                      background: "linear-gradient(135deg, #667eea, #764ba2)",
                      borderRadius: "50%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      margin: "0 auto",
                    }}
                  >
                    <i className="bi bi-clock text-white fs-4"></i>
                  </div>
                  <h5 className="fw-bold" style={{ color: "#333" }}>
                    Giờ Mở Cửa
                  </h5>
                  <p className="text-muted mb-0">
                    Thứ 2 - Thứ 6: 7:00 - 22:00
                    <br />
                    Cuối tuần: 8:00 - 23:00
                  </p>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </section>

      {/* ==================== FOOTER ==================== */}
      <footer style={{ background: "#1a1a2e", color: "#fff" }}>
        <Container className="py-5">
          <Row className="g-4">
            <Col lg={4}>
              <h4 className="fw-bold mb-3">
                <i
                  className="bi bi-cup-hot-fill me-2"
                  style={{ color: "#667eea" }}
                ></i>
                UC Study Coffee
              </h4>
              <p className="text-muted" style={{ lineHeight: "1.8" }}>
                Không gian làm việc và học tập lý tưởng. Nơi kết hợp hoàn hảo
                giữa sự chuyên nghiệp và hương vị cà phê thượng hạng.
              </p>
              <div className="d-flex gap-2">
                <a
                  href="#"
                  className="btn btn-sm"
                  style={{
                    width: "40px",
                    height: "40px",
                    background: "rgba(102,126,234,0.2)",
                    color: "#667eea",
                    borderRadius: "10px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <i className="bi bi-facebook"></i>
                </a>
                <a
                  href="#"
                  className="btn btn-sm"
                  style={{
                    width: "40px",
                    height: "40px",
                    background: "rgba(102,126,234,0.2)",
                    color: "#667eea",
                    borderRadius: "10px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <i className="bi bi-instagram"></i>
                </a>
                <a
                  href="#"
                  className="btn btn-sm"
                  style={{
                    width: "40px",
                    height: "40px",
                    background: "rgba(102,126,234,0.2)",
                    color: "#667eea",
                    borderRadius: "10px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <i className="bi bi-twitter"></i>
                </a>
                <a
                  href="#"
                  className="btn btn-sm"
                  style={{
                    width: "40px",
                    height: "40px",
                    background: "rgba(102,126,234,0.2)",
                    color: "#667eea",
                    borderRadius: "10px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <i className="bi bi-youtube"></i>
                </a>
              </div>
            </Col>
            <Col lg={2} md={6}>
              <h6
                className="fw-bold mb-3 text-uppercase"
                style={{ letterSpacing: "1px" }}
              >
                Liên Kết
              </h6>
              <ul className="list-unstyled">
                <li className="mb-2">
                  <Link to="/" className="text-muted text-decoration-none">
                    Trang Chủ
                  </Link>
                </li>
                <li className="mb-2">
                  <Link to="/menu" className="text-muted text-decoration-none">
                    Menu
                  </Link>
                </li>
                <li className="mb-2">
                  <Link
                    to="/booking"
                    className="text-muted text-decoration-none"
                  >
                    Đặt Chỗ
                  </Link>
                </li>
                <li className="mb-2">
                  <Link to="/login" className="text-muted text-decoration-none">
                    Đăng Nhập
                  </Link>
                </li>
              </ul>
            </Col>
            <Col lg={2} md={6}>
              <h6
                className="fw-bold mb-3 text-uppercase"
                style={{ letterSpacing: "1px" }}
              >
                Dịch Vụ
              </h6>
              <ul className="list-unstyled">
                <li className="mb-2">
                  <span className="text-muted">Workspace</span>
                </li>
                <li className="mb-2">
                  <span className="text-muted">Phòng Họp</span>
                </li>
                <li className="mb-2">
                  <span className="text-muted">Quiet Zone</span>
                </li>
                <li className="mb-2">
                  <span className="text-muted">Sự Kiện</span>
                </li>
              </ul>
            </Col>
            <Col lg={4}>
              <h6
                className="fw-bold mb-3 text-uppercase"
                style={{ letterSpacing: "1px" }}
              >
                Đăng Ký Nhận Tin
              </h6>
              <p className="text-muted small mb-3">
                Nhận thông tin ưu đãi và sự kiện mới nhất từ UC Study Coffee.
              </p>
              {emailSubmitted ? (
                <div className="alert alert-success py-2 px-3 rounded-pill mb-0 d-flex align-items-center">
                  <i className="bi bi-check-circle-fill me-2"></i>
                  <small>Đã đăng ký thành công!</small>
                </div>
              ) : (
                <form
                  onSubmit={handleNewsletterSignup}
                  className="d-flex gap-2"
                >
                  <input
                    type="email"
                    className="form-control rounded-pill"
                    placeholder="Email của bạn"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    style={{
                      background: "rgba(255,255,255,0.1)",
                      border: "1px solid rgba(255,255,255,0.2)",
                      color: "#333",
                    }}
                  />
                  <Button
                    type="submit"
                    className="rounded-pill px-4 fw-semibold border-0"
                    style={{
                      background: "linear-gradient(135deg, #667eea, #764ba2)",
                      whiteSpace: "nowrap",
                    }}
                  >
                    <i className="bi bi-send me-2"></i>Gửi
                  </Button>
                </form>
              )}
            </Col>
          </Row>
        </Container>
        <div style={{ background: "rgba(0,0,0,0.3)" }}>
          <Container className="py-3">
            <Row className="align-items-center">
              <Col className="text-center">
                <small className="text-muted">
                  &copy; 2026 UC Study Coffee. All rights reserved.
                </small>
              </Col>
            </Row>
          </Container>
        </div>
      </footer>

      {/* ==================== MODAL ==================== */}
      <Modal show={showModal} onHide={handleClose} centered>
        {selectedArea && areaDetails[selectedArea] && (
          <>
            <Modal.Header
              closeButton
              style={{
                background: "linear-gradient(135deg, #667eea, #764ba2)",
                color: "#fff",
              }}
            >
              <Modal.Title className="fw-bold">
                {areaDetails[selectedArea].title}
              </Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <p className="text-muted mb-4">
                {areaDetails[selectedArea].description}
              </p>
              <h6 className="fw-bold mb-3">Trạng thái các bàn:</h6>
              <ListGroup variant="flush">
                {areaDetails[selectedArea].tables.map((table, index) => (
                  <ListGroup.Item
                    key={index}
                    className="d-flex justify-content-between align-items-center"
                  >
                    <span>
                      <i
                        className={`bi bi-display me-2 ${
                          table.status === "available"
                            ? "text-success"
                            : "text-secondary"
                        }`}
                      ></i>
                      {table.id}
                    </span>
                    {table.status === "available" ? (
                      <Badge bg="success">Còn trống</Badge>
                    ) : (
                      <Badge bg="danger">Đang sử dụng</Badge>
                    )}
                  </ListGroup.Item>
                ))}
              </ListGroup>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={handleClose}>
                Đóng
              </Button>
              <Button
                onClick={handleBookingRedirect}
                style={{
                  background: "linear-gradient(135deg, #667eea, #764ba2)",
                  border: "none",
                }}
              >
                <i className="bi bi-calendar-check me-2"></i>Đặt chỗ tại đây
              </Button>
            </Modal.Footer>
          </>
        )}
      </Modal>
    </div>
  );
}

export default Home;
